import { ErrorHandler, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { AlertService } from '~core/services/alert.service';

// A lazy route's chunk is only fetched when the user navigates to it. Its filename
// carries a content hash, so once a new version is deployed the hashes a long-lived
// tab still holds are gone from the host. The SPA rewrite answers those requests with
// index.html rather than a 404, so webpack receives markup where it expects a script
// and rejects with a ChunkLoadError — the navigation dies and the button appears dead.
// Reloading is the recovery: it pulls the current index and its current chunk hashes.
const CHUNK_LOAD_PATTERNS = [
	/loading chunk \S+ failed/i,
	/failed to fetch dynamically imported module/i,
	/is not a valid javascript mime type/i,
];

// Reloading is only a fix when the tab is out of date. If the chunk is genuinely
// missing from the live deployment the error survives the reload, so a second reload
// within this window is suppressed in favour of telling the user.
const RELOAD_COOLDOWN_MS = 60_000;

const LAST_RELOAD_KEY = 'chunkReloadAt';

@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
	constructor(private readonly injector: Injector) {}

	public handleError(error: unknown): void {
		if (this.isBrowser() && this.isChunkLoadError(error)) {
			this.recoverFromStaleDeployment();
			return;
		}

		console.error(error);
	}

	private recoverFromStaleDeployment(): void {
		const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0);

		if (Date.now() - lastReload > RELOAD_COOLDOWN_MS) {
			sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
			this.reloadPage();
			return;
		}

		this.injector
			.get(AlertService)
			.error('Deze pagina kon niet geladen worden. Herlaad de pagina om verder te gaan.');
	}

	private reloadPage(): void {
		location.reload();
	}

	private isChunkLoadError(error: unknown): boolean {
		// Angular reports a rejected lazy-load as an Error wrapping the original
		// rejection, so the ChunkLoadError is one level down.
		const cause = (error as { rejection?: unknown })?.rejection ?? error;
		const name = (cause as { name?: string })?.name;
		const message = (cause as { message?: string })?.message ?? String(cause);

		return name === 'ChunkLoadError' || CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(message));
	}

	private isBrowser(): boolean {
		return isPlatformBrowser(this.injector.get(PLATFORM_ID));
	}
}
